// src/components/modal/SuspendUsersModal.js

import React from 'react';
import { Typography } from '@mui/material';
import { useCustomTheme } from '../../contexts/ThemeContext';
import { SuspendUserProfileProps, User } from '../../workers/ApiWorker';
import CustomModal from '../universal/modals/CustomModal';
import ErrorAlert from '../universal/alerts/ErrorAlert';
import TextInput from '../universal/inputs/TextInput';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useApi } from '../../contexts/ApiContext';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import Box from '@mui/material/Box';
import { SUSPEND_USERS_FAILED } from '../../helpers/Messages';

const createApiWorker = createWorkerFactory(
    () => import('../../workers/ApiWorker')
);

interface SuspendUsersModalProps {
    isMobile: boolean;
    isModalOpen: boolean;
    closeModal: (resetForm?: () => void) => void;
    setError: (error: string | undefined) => void; // Updated to match the state setter type
    selectedUsers: User[];
    error?: string;
    setUsers: (users: User[]) => void;
    users: User[];
}

// Define validation schema using Yup
const validationSchema = Yup.object({
    reason: Yup.string().required('Reason is required'),
});

const SuspendUsersModal: React.FC<SuspendUsersModalProps> = ({
    isMobile,
    isModalOpen,
    setError,
    error,
    selectedUsers,
    closeModal,
    setUsers,
    users,
}) => {
    const apiWorker = useWorker(createApiWorker);
    const { theme } = useCustomTheme();
    const { token } = useApi();

    const handleSuspendUsers = async (
        values: { reason: string },
        resetForm: () => void
    ) => {
        if (!token) {
            return;
        }

        const data: SuspendUserProfileProps = {
            ...values,
            uuids: selectedUsers.map(
                (selectedUser) => selectedUser.userProfile.uuid
            ),
        };

        try {
            const results = await apiWorker.suspendUserProfiles(data, token);

            // Create a new copy of selectedUsers and filter it based on UUIDs in alreadySuspended or failed
            const successfulSuspendedUsers = [...selectedUsers].filter(
                (selectedUser) => {
                    const selectedUserUuid = selectedUser.userProfile.uuid;
                    // Check if the UUID is in alreadySuspended or failed lists, and remove it if found
                    const isAlreadySuspended =
                        results.data.alreadySuspended.includes(
                            selectedUserUuid
                        );
                    const isFailed =
                        results.data.failed.includes(selectedUserUuid);
                    return !isAlreadySuspended && !isFailed;
                }
            );

            // Create a new copy of the 'users' array
            let newUsers = [...users]; // Assuming 'users' is the array of all users

            newUsers.forEach((newUser) => {
                const isSuspended = successfulSuspendedUsers.some(
                    (suspendedUser) =>
                        suspendedUser.userProfile.uuid ===
                        newUser.userProfile.uuid
                );

                if (isSuspended) {
                    // Update the accountStatus to 'suspended'
                    newUser.userProfile.accountStatus = 'suspended';
                }
            });

            setUsers(newUsers);
            // closeModal(resetForm);
        } catch (error) {
            setError(SUSPEND_USERS_FAILED);
        }
    };

    return (
        <Formik
            initialValues={{
                reason: '',
            }}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                await handleSuspendUsers(values, resetForm);
            }}
        >
            {({
                errors,
                touched,
                values,
                handleChange,
                isSubmitting,
                handleBlur,
                isValid,
                handleSubmit,
                resetForm,
            }) => (
                <CustomModal
                    isOpen={isModalOpen}
                    onClose={() => closeModal(resetForm)}
                    confirmLabel="Suspend"
                    onConfirm={handleSubmit}
                    isSubmitting={isSubmitting}
                    isValid={isValid}
                    isMobile={isMobile}
                >
                    {/* Modal content passed directly as children */}
                    <Typography
                        variant="h3"
                        color={theme.palette.text.secondary}
                    >
                        SUSPEND USERS
                    </Typography>
                    <Typography
                        variant="body2"
                        color={theme.palette.warning.main}
                        sx={{ marginBottom: 3 }}
                    >
                        Users who are already suspended will be ignored.
                    </Typography>
                    {error && <ErrorAlert message={error}></ErrorAlert>}
                    {selectedUsers.length > 0 && (
                        <>
                            <Typography sx={{ mt: 2 }} variant="body1">
                                Are you sure you want to suspend these users?
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 200,
                                    overflowY: 'auto', // Enable vertical scrolling
                                }}
                            >
                                <ul>
                                    {selectedUsers.map((x) => (
                                        <li key={x.uuid}>
                                            {x.userProfile.username}{' '}
                                            {x.userProfile.accountStatus ===
                                                'suspended' && (
                                                <span
                                                    style={{
                                                        color: 'red',
                                                    }}
                                                >
                                                    (Already Suspended)
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                            <Form>
                                <TextInput
                                    id={'reason'}
                                    label={'Reason'}
                                    value={values.reason}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    hasSubmitted={isSubmitting}
                                    touched={touched.reason}
                                    errors={errors.reason}
                                    enableLabel={true}
                                />
                            </Form>
                        </>
                    )}
                </CustomModal>
            )}
        </Formik>
    );
};

export default SuspendUsersModal;
